import React, { useEffect, useRef, useState } from "react";
import { Topbar } from "@/components/layout/Topbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import {
    UploadCloud, RefreshCcw, FileArchive, XCircle, CheckCircle2,
    Loader2, AlertTriangle, Zap, ChevronRight
} from "lucide-react";
import { api } from "@/lib/api";
import { StatusBadge } from "@/components/rikkus/StatusBadge";
import { TimestampET } from "@/components/rikkus/TimestampET";

const STATUS_MAP = {
    PENDING:   { kind: "pending",  label: "PENDIENTE" },
    PARSING:   { kind: "pending",  label: "PARSEANDO" },
    PARSED:    { kind: "hit",      label: "PARSEADO" },
    PARTIAL:   { kind: "partial",  label: "PARCIAL" },
    FAILED:    { kind: "miss",     label: "FALLIDO" },
    DUPLICATE: { kind: "neutral",  label: "DUPLICADO" },
};

function bytesToHuman(n) {
    if (n == null) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function BrisChartsPage() {
    const [uploads, setUploads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [error, setError] = useState(null);
    const [sweeping, setSweeping] = useState(false);
    const [expanded, setExpanded] = useState({});
    const fileInputRef = useRef(null);

    const load = () => {
        setLoading(true);
        setError(null);
        api.get("/bris-uploads?limit=100")
            .then((r) => setUploads(r.data || []))
            .catch((e) => setError(e?.response?.data?.detail || e.message))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
        const t = setInterval(() => {
            const inflight = uploads.some((u) => u.status === "PARSING" || u.status === "PENDING");
            if (inflight) load();
        }, 3000);
        return () => clearInterval(t);
    }, [uploads.some((u) => u.status === "PARSING" || u.status === "PENDING")]);

    const handleFiles = async (fileList) => {
        const files = Array.from(fileList || []);
        if (files.length === 0) return;
        const nonZip = files.find((f) => !/\.zip$/i.test(f.name));
        if (nonZip) {
            setError(`Solo se aceptan .zip (rechazado: ${nonZip.name})`);
            return;
        }
        setUploading(true);
        setError(null);
        try {
            const formData = new FormData();
            files.forEach((f) => formData.append("files", f));
            await api.post("/bris-uploads", formData, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 300000, // 5 min upload
            });
            load();
        } catch (e) {
            setError(e?.response?.data?.detail || e.message);
        } finally {
            setUploading(false);
        }
    };

    const runSweep = async (forceParsed = false) => {
        if (forceParsed && !window.confirm(
            "Vas a RE-PROCESAR todos los raw_captures BRIS ya en estado PARSED/PARTIAL. " +
            "Es idempotente (UPSERT no duplica), pero puede tardar. ¿Continuar?"
        )) return;
        setSweeping(true);
        setError(null);
        try {
            const qs = `include_failed=true&include_parsed=${forceParsed}&limit=500`;
            const r = await api.post(`/bris-uploads/sweep?${qs}`);
            const rep = r.data;
            alert(
                `Sweeper: reprocesados=${rep.reprocessed}\n` +
                `now_parsed=${rep.now_parsed}  now_partial=${rep.now_partial}  still_failed=${rep.still_failed}`
            );
            load();
        } catch (e) {
            setError(e?.response?.data?.detail || e.message);
        } finally {
            setSweeping(false);
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        handleFiles(e.dataTransfer.files);
    };

    return (
        <>
            <Topbar
                title="Charts BRIS — Comprehensive Charts"
                subtitle="Carga administrativa de Comprehensive Chart Files (Equibase-licensed)"
            />
            <div className="p-3 md:p-4 space-y-4 flex-1 overflow-auto">
                {/* Zona de carga drag-and-drop */}
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-sm flex items-center gap-2">
                            <UploadCloud className="h-4 w-4" /> Subir ZIPs
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div
                            data-testid="bris-dropzone"
                            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={onDrop}
                            onClick={() => fileInputRef.current?.click()}
                            className={
                                "border-2 border-dashed rounded-md p-8 text-center cursor-pointer transition-colors " +
                                (dragOver
                                    ? "border-primary bg-primary/5"
                                    : "border-border hover:border-primary/40 hover:bg-muted/20")
                            }
                        >
                            <input
                                data-testid="bris-file-input"
                                ref={fileInputRef}
                                type="file"
                                accept=".zip"
                                multiple
                                className="hidden"
                                onChange={(e) => handleFiles(e.target.files)}
                            />
                            {uploading ? (
                                <div className="flex flex-col items-center gap-2 text-sm text-muted-foreground">
                                    <Loader2 className="h-6 w-6 animate-spin" />
                                    Subiendo…
                                </div>
                            ) : (
                                <>
                                    <FileArchive className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                                    <div className="text-sm font-medium">
                                        Arrastrá acá los <code>.zip</code> o hacé click para elegir
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-1">
                                        Múltiples archivos y ZIP-de-ZIPs soportados. Máx 500 MB por request.
                                    </div>
                                </>
                            )}
                        </div>
                        {error && (
                            <div
                                data-testid="bris-error-banner"
                                className="mt-3 text-xs text-danger font-mono flex items-center gap-2"
                            >
                                <XCircle className="h-4 w-4" /> {error}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Tabla de uploads */}
                <Card>
                    <CardHeader className="pb-3 flex flex-row items-center justify-between">
                        <CardTitle className="text-sm">Historial de cargas</CardTitle>
                        <div className="flex gap-2">
                            <Button
                                data-testid="bris-sweep-btn"
                                variant="outline"
                                size="sm"
                                className="h-8 text-xs"
                                onClick={() => runSweep(false)}
                                disabled={sweeping}
                            >
                                {sweeping
                                    ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
                                    : <Zap className="h-3.5 w-3.5 mr-2" />} Reprocesar PENDING/FAILED
                            </Button>
                            <Button
                                data-testid="bris-force-reparse-btn"
                                variant="outline"
                                size="sm"
                                className="h-8 text-xs text-warn"
                                onClick={() => runSweep(true)}
                                disabled={sweeping}
                                title="Fuerza re-parseo de PARSED/PARTIAL (usar tras fixes al parser)"
                            >
                                <AlertTriangle className="h-3.5 w-3.5 mr-2" /> Forzar reproceso PARSED
                            </Button>
                            <Button
                                data-testid="bris-refresh-btn"
                                variant="outline"
                                size="sm"
                                className="h-8 text-xs"
                                onClick={load}
                            >
                                <RefreshCcw className="h-3.5 w-3.5 mr-2" /> Refrescar
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {loading && <Skeleton className="h-40 w-full" />}
                        {!loading && uploads.length === 0 && (
                            <div className="py-16 text-center text-sm text-muted-foreground">
                                No hay uploads todavía.
                            </div>
                        )}
                        {!loading && uploads.length > 0 && (
                            <div className="rounded-md border border-border overflow-hidden">
                                <Table data-testid="bris-uploads-table">
                                    <TableHeader>
                                        <TableRow className="bg-muted/40 hover:bg-muted/40">
                                            <TableHead className="h-8 text-[11px] w-6"></TableHead>
                                            <TableHead className="h-8 text-[11px]">Subido</TableHead>
                                            <TableHead className="h-8 text-[11px]">Archivo</TableHead>
                                            <TableHead className="h-8 text-[11px] text-right">Tamaño</TableHead>
                                            <TableHead className="h-8 text-[11px] text-right">ZIPs</TableHead>
                                            <TableHead className="h-8 text-[11px]">Pistas</TableHead>
                                            <TableHead className="h-8 text-[11px]">Fechas</TableHead>
                                            <TableHead className="h-8 text-[11px] text-right">Carr.</TableHead>
                                            <TableHead className="h-8 text-[11px] text-right">Corr.</TableHead>
                                            <TableHead className="h-8 text-[11px] text-right">SCR</TableHead>
                                            <TableHead className="h-8 text-[11px] text-right">DQ</TableHead>
                                            <TableHead className="h-8 text-[11px] text-right">DH</TableHead>
                                            <TableHead className="h-8 text-[11px]">Estado</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {uploads.map((u) => {
                                            const stat = STATUS_MAP[u.status] || { kind: "neutral", label: u.status };
                                            const isOpen = expanded[u.upload_id];
                                            return (
                                                <React.Fragment key={u.upload_id}>
                                                    <TableRow
                                                        data-testid={`bris-upload-row-${u.upload_id}`}
                                                        className="h-9 cursor-pointer"
                                                        onClick={() =>
                                                            setExpanded((x) => ({ ...x, [u.upload_id]: !x[u.upload_id] }))
                                                        }
                                                    >
                                                        <TableCell>
                                                            <ChevronRight
                                                                className={"h-3.5 w-3.5 text-muted-foreground transition-transform " + (isOpen ? "rotate-90" : "")}
                                                            />
                                                        </TableCell>
                                                        <TableCell className="text-xs">
                                                            <TimestampET value={u.uploaded_at} />
                                                        </TableCell>
                                                        <TableCell className="font-mono text-xs">
                                                            {u.original_filename}
                                                        </TableCell>
                                                        <TableCell className="text-right font-mono text-xs">
                                                            {bytesToHuman(u.byte_size)}
                                                        </TableCell>
                                                        <TableCell className="text-right font-mono text-xs">
                                                            {u.inner_zips_count}
                                                        </TableCell>
                                                        <TableCell className="text-xs">
                                                            {u.tracks_distinct?.length > 0
                                                                ? u.tracks_distinct.join(", ")
                                                                : "—"}
                                                        </TableCell>
                                                        <TableCell className="text-xs font-mono">
                                                            {u.dates_min
                                                                ? (u.dates_min === u.dates_max
                                                                    ? u.dates_min
                                                                    : `${u.dates_min} … ${u.dates_max}`)
                                                                : "—"}
                                                        </TableCell>
                                                        <TableCell className="text-right font-mono text-xs">
                                                            {u.races_ingested}
                                                        </TableCell>
                                                        <TableCell className="text-right font-mono text-xs">
                                                            {u.starters_ingested}
                                                        </TableCell>
                                                        <TableCell className="text-right font-mono text-xs text-muted-foreground">
                                                            {u.scratches_excluded}
                                                        </TableCell>
                                                        <TableCell className="text-right font-mono text-xs">
                                                            {u.dqs_seen > 0
                                                                ? <span className="text-warn">{u.dqs_seen}</span>
                                                                : <span className="text-muted-foreground">0</span>}
                                                        </TableCell>
                                                        <TableCell className="text-right font-mono text-xs">
                                                            {u.dead_heats_seen > 0
                                                                ? <span className="text-warn">{u.dead_heats_seen}</span>
                                                                : <span className="text-muted-foreground">0</span>}
                                                        </TableCell>
                                                        <TableCell>
                                                            <StatusBadge kind={stat.kind}>{stat.label}</StatusBadge>
                                                        </TableCell>
                                                    </TableRow>
                                                    {isOpen && (
                                                        <UploadDetailRow uploadId={u.upload_id} />
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="py-4 text-[11px] text-muted-foreground space-y-1">
                        <div><strong className="text-foreground">Reglas de ingesta:</strong></div>
                        <ul className="list-disc pl-4 space-y-0.5">
                            <li><code>finish_position</code> = campo 61 (Official Position), NUNCA el 60 (cross).</li>
                            <li>Scratches (<code>program=SCR</code> o <code>PP=99</code>) NO se guardan; se cuentan aparte.</li>
                            <li>DQ (campo 36 = Y): marcado con <code>disqualified=true</code>, sigue en el campo.</li>
                            <li>Dead heat (campo 80 = DH): posición duplicada permitida por diseño.</li>
                            <li>Resolución de caballo: SOLO por <code>equibase_id</code> (F81). Fila sin ID → parse_failures.</li>
                            <li>Idempotente por hash del ZIP y por natural key en <code>past_performances</code>.</li>
                        </ul>
                    </CardContent>
                </Card>
            </div>
        </>
    );
}


function UploadDetailRow({ uploadId }) {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        let cancel = false;
        setLoading(true);
        api.get(`/bris-uploads/${uploadId}`)
            .then((r) => { if (!cancel) setDetail(r.data); })
            .catch((e) => { if (!cancel) setDetail({ error: e?.response?.data?.detail || e.message }); })
            .finally(() => { if (!cancel) setLoading(false); });
        return () => { cancel = true; };
    }, [uploadId]);

    return (
        <TableRow
            data-testid={`bris-upload-detail-${uploadId}`}
            className="bg-muted/10 hover:bg-muted/10"
        >
            <TableCell colSpan={13} className="p-3">
                {loading && <Skeleton className="h-16 w-full" />}
                {!loading && detail && !detail.error && (
                    <div className="space-y-2">
                        <div className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
                            ZIPs internos ({detail.raw_captures?.length || 0})
                        </div>
                        <div className="rounded border border-border overflow-hidden">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-muted/40 hover:bg-muted/40">
                                        <TableHead className="h-7 text-[10px]">raw_id</TableHead>
                                        <TableHead className="h-7 text-[10px]">Archivo</TableHead>
                                        <TableHead className="h-7 text-[10px]">BRIS</TableHead>
                                        <TableHead className="h-7 text-[10px]">Track</TableHead>
                                        <TableHead className="h-7 text-[10px]">Fecha</TableHead>
                                        <TableHead className="h-7 text-[10px]">SHA-256</TableHead>
                                        <TableHead className="h-7 text-[10px]">Estado</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {(detail.raw_captures || []).map((rc) => (
                                        <TableRow key={rc.raw_capture_id} className="h-7">
                                            <TableCell className="font-mono text-[11px]">{rc.raw_capture_id}</TableCell>
                                            <TableCell className="font-mono text-[11px]">{rc.inner_filename}</TableCell>
                                            <TableCell className="font-mono text-[11px]">{rc.bris_track_code || "—"}</TableCell>
                                            <TableCell className="font-mono text-[11px]">{rc.track_id || "—"}</TableCell>
                                            <TableCell className="font-mono text-[11px]">{rc.race_date || "—"}</TableCell>
                                            <TableCell className="font-mono text-[10px] text-muted-foreground">
                                                {rc.payload_hash?.slice(0, 12)}…
                                            </TableCell>
                                            <TableCell>
                                                <StatusBadge
                                                    kind={STATUS_MAP[rc.parse_status]?.kind || "neutral"}
                                                >
                                                    {rc.parse_status}
                                                </StatusBadge>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                        {detail.recent_failures?.length > 0 && (
                            <>
                                <div className="text-[11px] font-medium text-warn uppercase tracking-wide flex items-center gap-1 pt-1">
                                    <AlertTriangle className="h-3 w-3" /> Errores ({detail.recent_failures.length})
                                </div>
                                <div className="rounded border border-border/50 bg-warn-bg/30 p-2 text-[10px] font-mono max-h-40 overflow-auto">
                                    {detail.recent_failures.map((f) => (
                                        <div key={f.failure_id}>
                                            [{f.error_type}] rc={f.raw_capture_id} — {f.error_message}
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                        {detail.error_summary && (
                            <div className="text-[10px] font-mono text-danger">
                                {detail.error_summary}
                            </div>
                        )}
                    </div>
                )}
                {!loading && detail?.error && (
                    <div className="text-xs font-mono text-danger">{detail.error}</div>
                )}
            </TableCell>
        </TableRow>
    );
}
